<template>
  <v-dialog :value="value" @input="$emit('input', $event)" max-width="720" scrollable>
    <v-card>
      <v-card-title v-if="billingInfo.billingInfoManagedByEledio">{{
        $t('global.billing.billingInformation')
      }}</v-card-title>
      <!-- <v-card-subtitle>
        {{ $t('global.welcomeMessage') }}
      </v-card-subtitle> -->

      <v-form ref="form" v-model="valid" class="dialog-body pa-6">
        <v-overlay :value="loading" absolute>
          <v-progress-circular indeterminate size="64"></v-progress-circular>
        </v-overlay>

        <!-- Billing data editing section - v-if so hidden inputs can't block validation -->
        <template v-if="billingInfo.billingInfoManagedByEledio">
          <v-text-field
            v-model="form.firstName"
            :label="'* ' + $t('global.billing.firstName')"
            :error-messages="firstNameErrors"
            @focus="showAllErrors"
          ></v-text-field>
          <v-text-field
            v-model="form.lastName"
            :label="'* ' + $t('global.billing.lastName')"
            :error-messages="lastNameErrors"
            @focus="showAllErrors"
          ></v-text-field>
          <v-text-field
            v-model="form.company"
            :label="$t('global.billing.company')"
            @focus="showAllErrors"
          ></v-text-field>
          <v-text-field
            v-model="form.companyId"
            :label="$t('global.billing.companyId')"
            @focus="showAllErrors"
          ></v-text-field>
          <v-text-field
            v-model="form.vat"
            :label="$t('global.billing.vat')"
            @focus="showAllErrors"
          ></v-text-field>
          <v-text-field
            v-model="form.email"
            :label="'* ' + $t('global.billing.email')"
            type="email"
            :error-messages="emailErrors"
            @focus="showAllErrors"
          ></v-text-field>
          <v-col v-if="invalidPrefilledPhone">
            <v-alert type="warning" outlined border="left" dense>
              {{ $t('global.billing.invalidSavedPhoneNotice') }}
            </v-alert>
          </v-col>
          <v-row>
            <v-col cols="4" sm="3">
              <v-text-field
                ref="phonePrefix"
                v-model="form.phonePrefix"
                :label="$t('global.billing.phonePrefix')"
                :rules="[rules.phonePrefix]"
                :error-messages="phonePrefixErrors"
                @focus="showAllErrors"
                placeholder="+420"
                @input="onPhonePrefixChange"
              />
            </v-col>
            <v-col cols="8" sm="9">
              <v-text-field
                ref="phoneNumber"
                v-model="form.phoneNumber"
                :label="$t('global.billing.phone')"
                :rules="[rules.phoneNumber]"
                :error-messages="phoneNumberErrors"
                @focus="showAllErrors"
                placeholder="602123456"
              />
            </v-col>
          </v-row>

          <v-select
            v-model="form.country"
            :items="countryItems"
            item-text="text"
            item-value="value"
            :label="'* ' + $t('global.billing.country')"
            :error-messages="countryErrors"
            @focus="showAllErrors"
          ></v-select>

          <v-row>
            <v-col cols="6">
              <v-text-field
                v-model="form.street"
                :label="$t('global.billing.street')"
                @focus="showAllErrors"
              ></v-text-field>
            </v-col>
            <v-col cols="3">
              <v-text-field
                v-model="form.streetNumber"
                :label="$t('global.billing.streetNumber')"
                :error-messages="streetNumberErrors"
                @focus="showAllErrors"
              ></v-text-field>
            </v-col>
            <v-col cols="3">
              <v-text-field
                v-model="form.apartmentNumber"
                :label="$t('global.billing.apartmentNumber')"
                :error-messages="apartmentNumberErrors"
                @focus="showAllErrors"
              ></v-text-field>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="6">
              <v-text-field
                v-model="form.zipCode"
                :label="$t('global.billing.zipCode')"
                @focus="showAllErrors"
              ></v-text-field>
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-model="form.city"
                :label="$t('global.billing.city')"
                @focus="showAllErrors"
              ></v-text-field>
            </v-col>
          </v-row>

          <!-- Currency Selection -->
          <v-row>
            <v-col cols="12">
              <v-select
                v-model="selectedCurrency"
                :items="availableCurrencies"
                :label="$t('global.selectedCurrency')"
                @change="updateDisplayedPrice"
                @focus="showAllErrors"
              ></v-select>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <v-select
                v-model="form.language"
                :label="$t('global.billing.invoiceLang')"
                dense
                :items="allowedLanguages"
                item-text="text"
                item-value="value"
                @focus="showAllErrors"
              />
            </v-col>
          </v-row>
        </template>

        <!-- Items List -->
        <v-row v-if="billingInfo.items && billingInfo.items.length > 0">
          <v-col cols="12">
            <v-card outlined class="mb-4">
              <v-card-title class="subtitle-1">{{ $t('global.billing.items') }}</v-card-title>
              <v-card-text>
                <v-list dense>
                  <v-list-item v-for="(item, index) in billingInfo.items" :key="index">
                    <v-list-item-content>
                      <v-list-item-title>{{ item.title }}</v-list-item-title>
                    </v-list-item-content>
                    <v-list-item-action>
                      <span class="font-weight-medium"
                        >{{ getItemPrice() }} {{ selectedCurrency }}</span
                      >
                    </v-list-item-action>
                  </v-list-item>
                </v-list>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Display Price in Selected Currency -->
        <v-row>
          <v-col cols="12">
            <v-card outlined>
              <v-card-text>
                <div v-if="billingInfo.items && billingInfo.items.length > 0" class="mb-2">
                  <div class="text-body-2 grey--text">
                    {{ getItemPrice() }} {{ selectedCurrency }} ×
                    {{ billingInfo.items.length }} items
                  </div>
                </div>
                <h3>{{ $t('global.price') }}: {{ displayedPrice }} {{ selectedCurrency }}</h3>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="onClose()" depressed>
            {{ $t('global.cancel') }}
          </v-btn>
          <v-btn color="orange" dark @click="submitForm" :disabled="!valid" depressed>
            {{ $t('global.confirm') }}
          </v-btn>
        </v-card-actions>
      </v-form>

      <!-- Invalid phone confirmation -->
      <v-dialog v-model="invalidPhoneDialog" max-width="500">
        <v-card>
          <v-card-title>{{ $t('global.formValidation.notPhoneNumber') }}</v-card-title>
          <v-card-text>
            {{ $t('global.billing.phone') }} – {{ $t('global.formValidation.notPhoneNumber') }}.
            {{ 'It will not be included in billing data.' }}
          </v-card-text>
          <v-card-actions class="justify-end">
            <v-btn text @click="invalidPhoneDialog = false">{{ $t('global.cancel') }}</v-btn>
            <v-btn color="primary" @click="proceedWithoutPhone">{{ $t('global.confirm') }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-card>
  </v-dialog>
</template>

<script>
import { subscriptionRenew } from '@/graphql/mutations/subscriptionRenew'
import { deviceBillingInfo } from '@/graphql/query/deviceBillingInfo'
import {
  isPhoneNumber,
  composeE164,
  splitE164,
  normalizePrefix,
} from '@/utils/phoneNumberValidator'
import {
  countryCodesWithName,
  countryCodesWithNameCs,
  countryCodesWithNamePl,
  countryCodesWithNameDe,
} from '@/config/countryCodes'

export default {
  name: 'SubscriptionRenewDialog',

  data() {
    return {
      valid: false,
      loading: false,
      form: {
        firstName: '',
        lastName: '',
        company: '',
        email: '',
        phonePrefix: '',
        phoneNumber: '',
        country: 'CZ',
        street: '',
        streetNumber: '',
        apartmentNumber: '',
        zipCode: '',
        city: '',
        companyId: '',
        vat: '',
        language: 'cze', // Default language for invoice
      },
      billingInfo: {},
      availableCurrencies: [], // New data property for available currencies
      selectedCurrency: 'CZK', // Default selected currency
      displayedPrice: 0, // Price to display based on selected currency
      showErrors: false,
      invalidPrefilledPhone: false,
      rules: {
        required: (value) => !!value || this.$t('global.formValidation.required'),
        // Do not block form submit via rules; we handle phone validation in submit with a popup
        phonePrefix: () => true,
        phoneNumber: () => true,
        numeric: (value) => {
          if (!value) return true // Allow empty numeric fields
          const pattern = /^\d+$/
          return pattern.test(value) || this.$t('global.formValidation.notNumeric')
        },
        email: (value) => {
          if (!value) return true // Allow empty email
          const pattern =
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
          return pattern.test(value) || this.$t('global.formValidation.notEmail')
        },
      },
      invalidPhoneDialog: false,
    }
  },

  mounted() {
    // Auto-validate fields that have values on mount
    this.$nextTick(() => {
      if (this.hasFormValues()) {
        this.showErrors = true
      }
    })
  },

  apollo: {
    billingInfo: {
      query: deviceBillingInfo,
      variables() {
        return {
          deviceId: this.device.id,
        }
      },
      update(data) {
        if (data?.billingInfo) {
          const billing = data.billingInfo

          const split = splitE164(billing.phone || '')

          this.form = {
            firstName: billing.firstName || '',
            lastName: billing.lastName || '',
            company: billing.company || '',
            email: billing.email || '',
            phonePrefix: split.prefix,
            phoneNumber: split.national,
            country: billing.country || 'CZ',
            street: billing.street || '',
            streetNumber: billing.streetNumber || '',
            apartmentNumber: billing.apartmentNumber || '',
            zipCode: billing.zipCode || '',
            city: billing.city || '',
            companyId: billing.companyId || '',
            vat: billing.vat || '',
            language: billing.invoiceLang || 'cze',
          }

          // If DB had phone but it's not valid, notify user it was omitted
          if (billing.phone && !isPhoneNumber(billing.phone)) {
            this.invalidPrefilledPhone = true
          } else {
            this.invalidPrefilledPhone = false
          }

          // Fetch available currencies from billing info
          this.availableCurrencies = billing.prices.map((price) => price.currency)
          this.billingInfo = billing

          this.selectedCurrency = billing.prices[0].currency

          this.displayedPrice = (billing.prices[0].price / 100).toFixed(2)
          this.updateDisplayedPrice()
        }
        return data?.billingInfo
      },
      loadingKey: 'loading',
    },
  },

  methods: {
    onPhonePrefixChange() {
      // Normalize prefix (strip spaces, convert 00 -> +) and re-validate
      this.form.phonePrefix = normalizePrefix(this.form.phonePrefix)
      if (this.$refs.phoneNumber && this.$refs.phoneNumber.validate) {
        this.$refs.phoneNumber.validate()
      }
    },
    async submitForm() {
      if (!this.$refs.form.validate()) {
        this.$toast.error(this.$t('global.fillRequiredFields'))
        return
      }
      const full = composeE164(this.form.phonePrefix, this.form.phoneNumber)
      const hasAnyPhone = !!(this.form.phonePrefix || this.form.phoneNumber)
      if (hasAnyPhone && !isPhoneNumber(full)) {
        this.invalidPhoneDialog = true
        return
      }
      await this.performSubmit(hasAnyPhone ? full : '')
    },
    async performSubmit(phone) {
      try {
        const paymentData = {
          firstName: this.form.firstName,
          lastName: this.form.lastName,
          company: this.form.company,
          phone: phone || '',
          country: this.form.country,
          street: this.form.street,
          streetNumber: this.form.streetNumber,
          apartmentNumber: this.form.apartmentNumber,
          zipCode: this.form.zipCode,
          city: this.form.city,
          email: this.form.email || '',
          companyId: this.form.companyId || '',
          vat: this.form.vat || '',
          currency: this.selectedCurrency,
          language: this.form.language,
        }

        const query = await this.$apollo.mutate({
          mutation: subscriptionRenew,
          variables: {
            paymentData,
            deviceId: this.device.id,
          },
        })

        const data = query.data

        if (data?.initTransaction) {
          const response = data.initTransaction
          if (response?.code === 0 || response?.code === '0') {
            this.$toast.success(this.$t('global.subscriptionRenewalSuccess'))
            if (response.redirect) {
              window.location.replace(response?.redirect)
            } else {
              this.onClose()
            }
          } else {
            this.$toast.error(response?.message || this.$t('global.subscriptionRenewalError'))
          }
        } else {
          this.$toast.error(this.$t('global.subscriptionRenewalError'))
        }
      } catch (error) {
        console.error('Error renewing subscription:', error)
        this.$toast.error(error.message || this.$t('global.subscriptionRenewalError'))
      }
    },
    proceedWithoutPhone() {
      this.invalidPhoneDialog = false
      this.performSubmit('')
    },
    showAllErrors() {
      this.showErrors = true
    },
    hasFormValues() {
      return (
        this.form.firstName ||
        this.form.lastName ||
        this.form.email ||
        this.form.phoneNumber ||
        this.form.country ||
        this.form.streetNumber ||
        this.form.apartmentNumber ||
        this.form.city
      )
    },
    updateDisplayedPrice() {
      // Calculate the base price in the selected currency
      const basePrice = this.billingInfo.prices.reduce((total, price) => {
        if (price.currency === this.selectedCurrency) {
          return total + price.price // Add price if it matches the selected currency
        }
        return total
      }, 0)

      // Multiply by number of items if items exist
      const rawItemCount = this.billingInfo.items ? this.billingInfo.items.length : 0
      const itemCount = Math.max(1, rawItemCount)
      const totalPrice = basePrice * itemCount

      // Convert total price from cents to the appropriate format (e.g., dollars)
      this.displayedPrice = (totalPrice / 100).toFixed(2) // Update displayed price in dollars
    },

    getItemPrice() {
      // Get the price per item in the selected currency
      const basePrice = this.billingInfo.prices.reduce((total, price) => {
        if (price.currency === this.selectedCurrency) {
          return total + price.price
        }
        return total
      }, 0)

      return (basePrice / 100).toFixed(2)
    },
  },
  watch: {
    'form.phonePrefix'() {
      const full = composeE164(this.form.phonePrefix, this.form.phoneNumber)
      if (full && isPhoneNumber(full)) this.invalidPrefilledPhone = false
    },
    'form.phoneNumber'() {
      const full = composeE164(this.form.phonePrefix, this.form.phoneNumber)
      if (full && isPhoneNumber(full)) this.invalidPrefilledPhone = false
    },
  },
  computed: {
    firstNameErrors() {
      if (!this.showErrors && !this.form.firstName) return []
      return !this.form.firstName ? [this.$t('global.formValidation.required')] : []
    },
    lastNameErrors() {
      if (!this.showErrors && !this.form.lastName) return []
      return !this.form.lastName ? [this.$t('global.formValidation.required')] : []
    },
    emailErrors() {
      if (!this.showErrors && !this.form.email) return []
      const errors = []
      if (!this.form.email) {
        errors.push(this.$t('global.formValidation.required'))
      } else if (this.rules.email(this.form.email) !== true) {
        errors.push(this.$t('global.formValidation.notEmail'))
      }
      return errors
    },
    phonePrefixErrors() {
      const hasAny = !!(this.form.phonePrefix || this.form.phoneNumber)
      if (!this.showErrors && !hasAny) return []
      if (!hasAny) return []
      if (!this.form.phonePrefix) return [this.$t('global.formValidation.required')]
      if (!/^\+[1-9]\d{0,3}$/.test(this.form.phonePrefix))
        return [this.$t('global.formValidation.notPhoneNumber')]
      return []
    },
    phoneNumberErrors() {
      const hasAny = !!(this.form.phonePrefix || this.form.phoneNumber)
      if (!this.showErrors && !hasAny) return []
      if (!hasAny) return []
      if (!this.form.phoneNumber) return [this.$t('global.formValidation.required')]
      const full = (this.form.phonePrefix || '') + String(this.form.phoneNumber).replace(/\s+/g, '')
      if (!isPhoneNumber(full)) return [this.$t('global.formValidation.notPhoneNumber')]
      return []
    },
    countryErrors() {
      if (!this.showErrors && !this.form.country) return []
      return !this.form.country ? [this.$t('global.formValidation.required')] : []
    },
    streetNumberErrors() {
      if (!this.form.streetNumber) return []
      return this.rules.numeric(this.form.streetNumber) === true
        ? []
        : [this.$t('global.formValidation.notNumeric')]
    },
    apartmentNumberErrors() {
      if (!this.form.apartmentNumber) return []
      return this.rules.numeric(this.form.apartmentNumber) === true
        ? []
        : [this.$t('global.formValidation.notNumeric')]
    },
    cityErrors() {
      // City is no longer required
      return []
    },
    countryItems() {
      let countryData = countryCodesWithName
      const locale = this.$i18n.locale

      switch (locale) {
        case 'cs':
          countryData = countryCodesWithNameCs
          break
        case 'pl':
          countryData = countryCodesWithNamePl
          break
        case 'de':
          countryData = countryCodesWithNameDe
          break
        default:
          countryData = countryCodesWithName
      }

      return Object.entries(countryData).map(([code, name]) => ({
        text: name,
        value: code,
      }))
    },
    allowedLanguages() {
      return [
        {
          text: this.$t('global.lang.cs'),
          value: 'cze',
        },
        {
          text: this.$t('global.lang.en'),
          value: 'eng',
        },
        {
          text: this.$t('global.lang.de'),
          value: 'deu',
        },
        {
          text: this.$t('global.lang.pl'),
          value: 'pol',
        },
      ]
    },
  },
  props: {
    value: Boolean,
    device: {
      type: Object,
      required: true,
    },
    onClose: Function,
  },
}
</script>

<style scoped>
.v-container {
  max-width: 600px;
}

.dialog-body {
  max-height: 75vh;
  overflow-y: auto;
}
</style>
